/**
 * A DATA CENTRE'S POWER, NOW — the figures a bank's operator opens the Overview for (Hamzah,
 * 2026-10-15: "it's for banks"). What is the room running on, how much is it drawing, how much of
 * that is the UPS load, is the generator ready, and is any feeder or meter quiet.
 *
 * KEYED ON THE DEVICE'S ROLE, never on its name. A meter is a `utility_feed` (an incomer from the
 * grid), a `ups` (a feeder to a UPS), a `pdu` (a PDU's own input metering), or `switchgear` (any
 * other metered panel); the generator is `generator`; the DC charger or DC plant is `rectifier`; a
 * UPS that reports only its own load is a `ups` with no power reading, read by `upsUnitsNow`. The roles are set in Device management, so the same
 * code reads a second bank's site the day its devices are given roles, and a wrong total is fixed
 * by fixing a role, never by an `if` here.
 *
 * THE READINGS are the gateway's own keys (`overview.py`, the v2 "overview" payload): a panel meter
 * reports `active_power` per phase (and `total_system_power`), `currents`, `line_to_line_volts`,
 * `frequency`, `total_system_pf`, `connection_percentage`; the generator controller
 * `generator_total_power`, `speed`, `generator_frequency`, `fuel`, `battery`,
 * `coolant_temperature`, its cumulative `runtime` counter and its `error_details_*` flags; the DC
 * charger `dpp_channels_voltage`, `dpp_channels_current`, `dpp_channels_temperature`. The gateway's
 * own `counters_time_on_generator_today_m` is NOT read: it stayed 0 through every run it was asked
 * about (`runTodayOf`).
 *
 * A COMBINED DEVICE (Integrations → Combine devices) is one feeder however it was made. One that
 * ADDS UP its members (0109) carries readings of its own under these same keys, so it reads like any
 * meter, and its members are not counted again on their own (`countedInCombines`). One that only
 * SHOWS its members' registers carries each member's reading under one key — two channels, two
 * `total_system_power` — so a feeder's readings are read per physical meter (`sourceDeviceId`) and
 * added, never the first one taken for the whole: that read half of a transformer incomer on Inala's
 * prod (2026-10-17).
 *
 * ANOTHER GATEWAY'S KEYS (2026-10-20). A telecom site's gateway sends the same quantities under its
 * own names; `dc-vocabulary.ts` reads them as the keys above before any figure here is worked out,
 * so every rule below serves both. Where that site has no meter at the grid incomer (JAZZ MSC5), the
 * supply is read from the transfer switch's mains witness and the load from what is metered below it
 * (`supplyNow`, `measuredLoadKw`), and the card says which.
 *
 * Pure: rows and devices in, figures out — so every rule here is tested without a screen.
 */
import type { LatestRow, RangeResponse } from '@/lib/api/types';
import { generatorMode, type GeneratorMode } from '@/modules/sites/pages/generator-mode';
import { engineTurning } from '@/modules/sites/pages/data-centre-analytics';
import { DEAD_PHASE_V } from '@/modules/sites/pages/dc-continuity';
import { pduLabel } from './pdu-label';
import { dcSystemIds } from './dc-systems';
import { hoursFromUnit } from '@/modules/sites/pages/run-hours';

export const FEEDER_ROLES = ['utility_feed', 'ups', 'pdu', 'switchgear'] as const;
export type FeederRole = (typeof FEEDER_ROLES)[number];

/** The keys this page reads, asked of `/telemetry/latest` in one request. */
export const DATA_CENTRE_KEYS = [
  'active_power',
  'total_system_power',
  'total_import_kwh',
  'currents',
  'line_to_line_volts',
  'frequency',
  'total_system_pf',
  'power_factor',
  'connection_percentage',
  'generator_total_power',
  'speed',
  'generator_frequency',
  'fuel',
  'battery',
  'coolant_temperature',
  'runtime',
  'dpp_channels_voltage',
  'dpp_channels_current',
  'dpp_channels_temperature',
  'mains_phase_voltages',
  'control_mode',
] as const;

/**
 * The generator controller's fault flags (a DSE controller through the gateway), 1 while raised.
 * Asked for by name: the latest endpoint takes whole keys, not a prefix.
 */
export const GENERATOR_FAULT_KEYS = [
  'battery_high_voltage', 'battery_low_voltage', 'charge_alternator_failure', 'emergency_stop', 'fail_to_start',
  'fail_to_stop', 'generator_fail_to_close', 'generator_high_current', 'generator_high_voltage',
  'generator_low_voltage', 'generator_over_frequency', 'generator_short_circuit', 'generator_under_frequency',
  'high_coolant_temperature', 'low_coolant_temperature', 'low_fuel_level', 'low_oil_level_switch',
  'low_oil_pressure', 'mains_fail_to_close', 'mains_high_current', 'mains_short_circuit', 'maintenance_alarm',
  'over_speed', 'under_speed',
].map((k) => `error_details_${k}`);

/** Bands for a 400 V, 50 Hz supply: the grid card's own (207 to 245 V phase is 360 to 424 V line). */
export const LINE_VOLTS_BAND = [360, 424] as const;
/** The same band read phase to neutral, for a witness that reads the phases (a transfer switch's source 1). */
export const PHASE_VOLTS_BAND = [207, 245] as const;
export const HZ_BAND = [49.5, 50.5] as const;
/** A power factor this low costs the site in losses and, on most tariffs, in charges. */
export const PF_FLOOR = 0.85;
/** Fuel at or under this is marked: a quarter tank is a few hours under load. */
export const FUEL_LOW_PCT = 25;
/** Under this a feeder is carrying nothing worth a share of the total. */
export const IDLE_KW = 0.5;

export interface DeviceRole {
  id: number;
  name: string;
  role: string | null;
}

export interface FeederNow {
  deviceId: number;
  /** Which unit of a device that carries several (two PDUs behind one agent); absent for one meter. */
  unit?: number;
  /** The device's own name ("Huawei PDU8000 Main+Module Metering, unit 1"): the tooltip and aria-label. */
  name: string;
  /** What the DC screens call it ("PDU unit 1", `pduLabel`); absent where that is its name. */
  label?: string;
  role: FeederRole;
  kw: number | null;
  /** The highest phase current: the phase nearest its limit is the one that matters. */
  amps: number | null;
  /** Mean of the three line-to-line voltages. */
  volts: number | null;
  pf: number | null;
  hz: number | null;
  /** False when the gateway says it cannot read this meter (`connection_percentage` 0). */
  reading: boolean;
}

export interface GeneratorNow {
  deviceId: number;
  name: string;
  /** Null when nothing it reports says either way. */
  running: boolean | null;
  /** Its output: the controller's total, or its three phases added when it sends no total. */
  kw: number | null;
  fuelPct: number | null;
  batteryV: number | null;
  coolantC: number | null;
  /** Faults the controller raises now, in words ("Low fuel level"). */
  faults: string[];
  /** The controller's mode, through its value labels (`generatorMode`) or its lit mode lamp; null when not reported. */
  mode: GeneratorMode | null;
  /** The engine's hour meter, in hours. */
  runHours: number | null;
  /** The controller's count of engine starts. */
  starts: number | null;
  /** Something it sent is fresh. False: every figure above is null, and `lastAt` says since when. */
  heard: boolean;
  /** Its newest reading's time, fresh or not; null when it never reported. */
  lastAt: number | null;
}

export interface ChargerNow {
  deviceId: number;
  name: string;
  volts: number | null;
  amps: number | null;
  tempC: number | null;
  /** Something it sent is fresh; see `GeneratorNow.heard`. */
  heard: boolean;
  lastAt: number | null;
}

export type Supply = 'grid' | 'generator' | 'both' | 'battery' | 'none';

export interface SupplyNow {
  /** Null while nothing has been read. */
  source: Supply | null;
  /** Every grid incomer summed; null when any of them is not read now. */
  gridKw: number | null;
  /** The generator's share: its output while it runs, 0 while it stands; null when its state is not read. */
  generatorKw: number | null;
  /**
   * What the site draws: grid plus generator where the incomers are metered (`measuredAt: 'incomers'`);
   * where they are not, what is metered below them, the PDUs and the DC plants (`'loads'`), which is
   * the protected load rather than everything the site takes from the grid. Null when a part is not read.
   */
  siteKw: number | null;
  measuredAt: 'incomers' | 'loads' | null;
  /** Every UPS feeder summed; null when any of them is not read now. */
  upsKw: number | null;
  /** What a total above is waiting for: the feeders not read now, and the generator when its state is not. */
  unread: string[];
}

/** The mains as the transfer switch or the generator controller sees it, where no incomer is metered. */
export interface MainsNow {
  /** Mean phase voltage across the phases that report. */
  volts: number | null;
  hz: number | null;
  /** At least half the nominal phase voltage (`DEAD_PHASE_V`): the grid is there. */
  live: boolean | null;
}

const num = (v: number | null | undefined): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const sum = (vs: ReadonlyArray<number | null>): number | null => {
  const got = vs.filter((v): v is number => v !== null);
  return got.length === 0 ? null : got.reduce((a, b) => a + b, 0);
};
const mean = (vs: ReadonlyArray<number | null>): number | null => {
  const got = vs.filter((v): v is number => v !== null);
  return got.length === 0 ? null : got.reduce((a, b) => a + b, 0) / got.length;
};
const max = (vs: ReadonlyArray<number | null>): number | null => {
  const got = vs.filter((v): v is number => v !== null);
  return got.length === 0 ? null : Math.max(...got);
};

/** The device's readings, by key: every instance of a key, in instance order. */
function byKey(rows: readonly LatestRow[], deviceId: number): Map<string, Array<number | null>> {
  const out = new Map<string, Array<{ i: number; v: number | null }>>();
  for (const r of rows) {
    if (r.deviceId !== deviceId) continue;
    const list = out.get(r.baseKey) ?? [];
    list.push({ i: r.instance, v: num(r.value) });
    out.set(r.baseKey, list);
  }
  return new Map([...out].map(([k, list]) => [k, list.sort((a, b) => a.i - b.i).map((x) => x.v)]));
}

const one = (m: Map<string, Array<number | null>>, key: string): number | null => m.get(key)?.[0] ?? null;

/**
 * A device's FRESH readings by key, whether anything it sent is fresh, and when it last sent.
 *
 * NEVER A LAST VALUE AS NOW (Hamzah, 2026-10-20: "the data should be 100% real"). The latest table
 * keeps a silent device's last values, and the generator and the DC charger were read from it whole:
 * a controller gone quiet would have kept showing its fuel, its battery and "Stopped" as if read a
 * minute ago. A stale row (older than the API's `maxAgeSeconds`) is left out, as `byMeter` leaves it
 * out of a feeder, and the card says since when it has not been read.
 */
function freshOf(
  rows: readonly LatestRow[],
  deviceId: number,
): { m: Map<string, Array<number | null>>; fresh: LatestRow[]; heard: boolean; reading: boolean; lastAt: number | null } {
  const own = rows.filter((r) => r.deviceId === deviceId);
  const fresh = own.filter((r) => !r.stale);
  const times = own.map((r) => Date.parse(r.ts)).filter((t) => Number.isFinite(t));
  const heard = fresh.length > 0;
  return { m: byKey(fresh, deviceId), fresh, heard, reading: heard, lastAt: times.length > 0 ? Math.max(...times) : null };
}

/**
 * The device's readings split by the PHYSICAL meter they came from: one map for a wire device or a
 * combine that adds up, one per member for a combine that shows its members' registers together.
 *
 * ONLY WHAT IS FRESH COUNTS (2026-10-19). A meter in comms loss keeps its last values in the
 * latest table; on its own it reads "Not reading" through its `connection_percentage`, but a combine
 * that shows only its power register carries no such reading, and a generator incomer silent since
 * Oct 16 was added as 133 kW live. So a stale row (older than the API's `maxAgeSeconds`) is left
 * out of the figures, and a meter with nothing fresh is `heard: false`.
 */
function byMeter(rows: readonly LatestRow[], deviceId: number): Array<{ m: Map<string, Array<number | null>>; heard: boolean }> {
  const meters = new Map<number, LatestRow[]>();
  for (const r of rows) {
    if (r.deviceId !== deviceId) continue;
    const meter = r.sourceDeviceId ?? r.deviceId;
    const list = meters.get(meter) ?? [];
    list.push(r);
    meters.set(meter, list);
  }
  return [...meters.values()].map((list) => ({
    m: byKey(list.filter((r) => !r.stale), deviceId),
    heard: list.some((r) => !r.stale),
  }));
}

/** Phase by phase across meters: phase 1 of each added, then phase 2, … */
const byPhase = (lists: ReadonlyArray<ReadonlyArray<number | null>>): Array<number | null> =>
  Array.from({ length: Math.max(0, ...lists.map((l) => l.length)) }, (_, i) => sum(lists.map((l) => l[i] ?? null)));

const isFeeder = (role: string | null): role is FeederRole => (FEEDER_ROLES as readonly string[]).includes(role ?? '');

/** A site's combine as the Overview needs it: which device it made, how, and from which devices. */
export interface CombineMembers {
  deviceId: number;
  mode: 'show' | 'maths';
  sources: ReadonlyArray<{ deviceId: number }>;
}

/**
 * The devices a combine that ADDS UP already counts as a feeder: its members, whose readings are in
 * its own. Counting them again would add each incomer two or three times over. A combine that only
 * shows registers counts nothing of its own, so its members stand; nor does one with no feeder role.
 */
export function countedInCombines(combines: readonly CombineMembers[], devices: readonly DeviceRole[]): Set<number> {
  const out = new Set<number>();
  for (const c of combines) {
    if (c.mode !== 'maths') continue;
    if (!isFeeder(devices.find((d) => d.id === c.deviceId)?.role ?? null)) continue;
    for (const s of c.sources) out.add(s.deviceId);
  }
  return out;
}

/**
 * Every metered feeder now, grid incomers first, then the UPS feeders, then the other panels.
 * `counted` are devices another feeder already counts (`countedInCombines`), left out.
 */
export function feedersNow(
  rows: readonly LatestRow[],
  devices: readonly DeviceRole[],
  counted: ReadonlySet<number> = new Set(),
): FeederNow[] {
  const order: Record<FeederRole, number> = { utility_feed: 0, ups: 1, pdu: 2, switchgear: 3 };
  /* A UPS that sends its own load and no power reading is a UPS, not a meter on the feeder to one
     (`upsUnitsNow` reads it). Only that: a meter the gateway cannot read stays, as "Not reading". */
  const upsUnit = new Set(rows.filter((r) => r.baseKey === 'ups_load_pct').map((r) => r.deviceId));
  const metersPower = new Set(rows.filter((r) => r.baseKey === 'total_system_power' || r.baseKey === 'active_power').map((r) => r.deviceId));
  const pduIds = devices.filter((d) => d.role === 'pdu' && !counted.has(d.id)).map((d) => d.id);
  return devices
    .filter((d): d is DeviceRole & { role: FeederRole } => isFeeder(d.role) && !counted.has(d.id))
    .filter((d) => !upsUnit.has(d.id) || metersPower.has(d.id))
    .flatMap((d) => {
      /* Two PDUs behind one agent are two feeders, each with its own serial: their meters are the
         vocabulary's per-unit ids (negative, `unitMeterId`). A combine's members stay one feeder. */
      const units = [...new Set(rows.filter((r) => r.deviceId === d.id && (r.sourceDeviceId ?? 0) < 0).map((r) => r.sourceDeviceId as number))].sort((a, b) => b - a);
      if (units.length < 2) return [{ d, rows, unit: null as number | null, n: null as number | null }];
      return units.map((u, i) => ({ d: { ...d, name: `${d.name}, unit ${i + 1}` }, rows: rows.filter((r) => r.deviceId !== d.id || r.sourceDeviceId === u), unit: u, n: i + 1 }));
    })
    .map(({ d, rows, unit, n }) => {
      const meters = byMeter(rows, d.id);
      const maps = meters.map((x) => x.m);
      const conns = maps.map((m) => one(m, 'connection_percentage'));
      return {
        deviceId: d.id,
        ...(unit === null ? {} : { unit }),
        name: d.name,
        ...(d.role === 'pdu' ? { label: pduLabel(pduIds, d.id, n) } : {}),
        role: d.role,
        kw: sum(maps.map((m) => one(m, 'total_system_power') ?? sum(m.get('active_power') ?? []))),
        /* The phase nearest its limit, with every meter's current on that phase added. */
        amps: max(byPhase(maps.map((m) => m.get('currents') ?? []))),
        volts: mean(maps.flatMap((m) => m.get('line_to_line_volts') ?? [])),
        pf: mean(maps.map((m) => one(m, 'total_system_pf') ?? mean(m.get('power_factor') ?? []))),
        hz: maps.map((m) => one(m, 'frequency')).find((v) => v !== null) ?? null,
        /* Read only when every meter is: one channel of two silent is half the incomer. */
        reading: meters.length > 0 && meters.every((x, i) => x.heard && (conns[i] === null || (conns[i] as number) > 0)),
      };
    })
    .sort((a, b) => order[a.role] - order[b.role] || a.name.localeCompare(b.name));
}

/** A controller's common alarm lamp lit, where it names no fault of its own: a fault, unnamed. */
export const COMMON_ALARM_WORDS = 'Common alarm lamp lit';

/** "error_details_low_fuel_level" → "Low fuel level". */
export function faultWords(key: string): string {
  const words = key.replace(/^error_details_/, '').replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The mode from the controller's mode lamps (a DSE's Auto, Manual and Stop), where it sends no mode
 * register. Only a lit lamp says anything: none lit is not reported, never "Stop".
 */
export function generatorModeFromLamps(m: Map<string, Array<number | null>>): GeneratorMode | null {
  const lit = (k: string): boolean => one(m, k) === 1;
  if (lit('mode_lamp_auto')) return { text: 'Auto', named: true, auto: true };
  if (lit('mode_lamp_manual')) return { text: 'Manual', named: true, auto: false };
  if (lit('mode_lamp_stop')) return { text: 'Stop', named: true, auto: false };
  return null;
}

/**
 * Every generator on the site, now, in the order Device management lists them: the first is
 * `generatorNow`'s. A site with two sets (JAZZ's DSE and Cummins, a bank's standby and emergency)
 * adds both into the supply (`supplyNow`), so no generator's output is left out of the site load.
 */
/**
 * A STARTER BATTERY'S 0 V IS NOT A READING (2026-10-20). JAZZ's Cummins PCC3300 stored an exact 0 V six
 * times in 68 readings on its first day, each between 26.4 and 26.6 V three minutes either side, and
 * the newest at 21:31 was one of them: the Backup tab said "Stopped: battery 0.0 V" and its readiness
 * line dived to 0. A generator controller is powered by its own starter battery, so a controller that
 * is reporting cannot be reading that battery at 0 V; the zeros are the gateway's failed reads. An
 * exact 0 from a controller is therefore no reading (null, "not read"), and a 0 in a trend is a gap,
 * never a line to zero. Any controller, not the PCC by name; a genuinely low battery (18 V) is a
 * reading and still shows, marked. The console's own place for this, a parameter's plausibility
 * floor, is not enforced on any read or ingest path yet (`PATCH /metrics-config`), so it lives here.
 */
export function starterVolts(v: number | null | undefined): number | null {
  return v === undefined || v === null || v === 0 ? null : v;
}

/** A starter battery's trend with its failed reads (an exact 0) made gaps: `starterVolts`, point by point. */
export function starterPoints<P extends { v: number | null }>(points: readonly P[]): P[] {
  return points.map((p) => (p.v === 0 ? { ...p, v: null } : p));
}

export function generatorsNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): GeneratorNow[] {
  return devices.filter((x) => x.role === 'generator').map((d) => generatorOf(rows, d));
}

/**
 * What a generator adds to the site load now: its output while its engine runs, 0 while it stands,
 * null while its state is not read. One rule for the site total and for any picture of it.
 */
export function generatorShareKw(g: GeneratorNow): number | null {
  return g.running === true ? g.kw : g.running === false ? 0 : null;
}

/** The first generator on the site, now. Null when the site has none. */
export function generatorNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): GeneratorNow | null {
  return generatorsNow(rows, devices)[0] ?? null;
}

function generatorOf(rows: readonly LatestRow[], d: DeviceRole): GeneratorNow {
  /* Fresh rows only (`freshOf`): a controller gone quiet is not read, never its last values as now. */
  const { m, fresh, heard, lastAt } = freshOf(rows, d.id);
  /* A total where the controller sends one; else its phases added, and only when at least one reports. */
  const kw = one(m, 'generator_total_power') ?? sum(m.get('generator_phase_power') ?? []);
  const speed = one(m, 'speed');
  const hz = one(m, 'generator_frequency');
  const flag = one(m, 'generator_running');
  /* The controller's own run flag first; then the engine (`engineTurning`); the output register only
     when the controller reports neither, because a DSE reads the LOAD there whichever source carries it. */
  const running = (flag === null ? null : flag === 1) ?? engineTurning(speed, hz) ?? (kw === null ? null : kw > IDLE_KW);
  const secs = one(m, 'engine_run_seconds');
  const named = fresh
    .filter((r) => r.baseKey.startsWith('error_details_') && num(r.value) === 1)
    .map((r) => faultWords(r.baseKey))
    .sort();
  /* A DSE names no fault over the wire: its common alarm lamp (`dse_dg_led`) is the one it lights for
     any of them. Unread, Demo DC 2's set that failed to start read "Ready, in Auto" beside it (2026-10-21). */
  const faults = named.length === 0 && one(m, 'common_alarm_lamp') === 1 ? [COMMON_ALARM_WORDS] : named;
  return {
    deviceId: d.id,
    name: d.name,
    running,
    kw,
    fuelPct: one(m, 'fuel'),
    batteryV: starterVolts(one(m, 'battery')),
    coolantC: one(m, 'coolant_temperature'),
    faults,
    mode: generatorMode(fresh.find((r) => r.baseKey === 'control_mode')) ?? generatorModeFromLamps(m),
    runHours: one(m, 'engine_run_hours') ?? (secs === null ? null : secs / 3600),
    starts: one(m, 'successful_start_attempts'),
    heard,
    lastAt,
  };
}

/** The DC charger (role `rectifier`), now. Null when the site has none. */
export function chargerNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): ChargerNow | null {
  const d = devices.find((x) => x.role === 'rectifier');
  if (!d) return null;
  const { m, heard, lastAt } = freshOf(rows, d.id);
  return {
    deviceId: d.id,
    name: d.name,
    volts: one(m, 'dpp_channels_voltage'),
    amps: one(m, 'dpp_channels_current'),
    tempC: one(m, 'dpp_channels_temperature'),
    heard,
    lastAt,
  };
}

/**
 * The mains, from the generator controllers' and transfer switches' view of it (`mains_phase_voltages`,
 * fresh rows only). Null when no device on the site witnesses the mains.
 */
export function mainsNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): MainsNow | null {
  const ids = new Set(devices.filter((d) => d.role === 'generator').map((d) => d.id));
  const fresh = rows.filter((r) => ids.has(r.deviceId) && !r.stale);
  const volts = mean(fresh.filter((r) => r.baseKey === 'mains_phase_voltages').map((r) => num(r.value)));
  const hz = mean(fresh.filter((r) => r.baseKey === 'mains_frequency').map((r) => num(r.value)));
  if (volts === null && hz === null) return null;
  return { volts, hz, live: volts === null ? null : volts >= DEAD_PHASE_V };
}

/**
 * What is metered below the incomers, now: the PDUs' input power and the DC plants' load, added.
 * They are separate branches (the PDUs carry the AC racks, the DC plants the -48 V load), so nothing
 * is counted twice. Every member or nothing, as `supplyNow`'s totals: a PDU unit or a plant not read
 * now leaves the load empty rather than smaller by its share. Null when neither reports.
 */
export function measuredLoadKw(feeders: readonly FeederNow[], plants: readonly DcPlantNow[]): number | null {
  const pdus = feeders.filter((f) => f.role === 'pdu');
  if (pdus.some((f) => !f.reading || f.kw === null) || plants.some((p) => !p.reading || p.loadKw === null)) return null;
  return sum([sum(pdus.map((f) => f.kw)), sum(plants.map((p) => p.loadKw))]);
}

/**
 * What the site runs on and draws, now. `gen` is the site's generator, or every one of them
 * (`generatorsNow`): each adds its share (`generatorShareKw`), and one whose state is not read
 * leaves the total empty and named, as an incomer not read does. Where no grid incomer is metered,
 * the mains witness (`mainsNow`) says whether the grid is there, and the load is what is metered
 * below it.
 */
export function supplyNow(
  feeders: readonly FeederNow[],
  gen: GeneratorNow | readonly GeneratorNow[] | null,
  mains: MainsNow | null = null,
  belowKw: number | null = null,
  /** A battery system discharging by evidence (`anyDischarging`), as the status strip reads it. */
  batteryDischarging = false,
): SupplyNow {
  const gens: readonly GeneratorNow[] = gen === null ? [] : 'deviceId' in gen ? [gen] : gen;
  /*
   * A TOTAL IS EVERY MEMBER OR NOTHING (Hamzah, 2026-10-20). Summing only the incomers that read
   * made one silent incomer read as the site's load falling by its share; a generator whose state
   * was not read was added as 0 kW. A part not read now leaves its total empty, and `unread` names
   * it, so the card says what the figure is waiting for instead of showing a smaller one.
   */
  const total = (role: FeederRole): { kw: number | null; unread: string[] } => {
    const members = feeders.filter((f) => f.role === role);
    const unread = members.filter((f) => !f.reading || f.kw === null).map((f) => f.name);
    return { kw: members.length === 0 || unread.length > 0 ? null : sum(members.map((f) => f.kw)), unread };
  };
  const grid = total('utility_feed');
  const ups = total('ups');
  const metered = feeders.some((f) => f.role === 'utility_feed');
  const shares = gens.map(generatorShareKw);
  const genUnread = gens.filter((_, k) => shares[k] === null);
  const generatorKw = gens.length === 0 || genUnread.length > 0 ? null : sum(shares);
  /* Whether the grid carries load: any incomer that reads above idle, even with another silent; with
     no incomer metered, the mains witness. */
  const gridRead = metered ? feeders.some((f) => f.role === 'utility_feed' && f.reading) : mains !== null && mains.live !== null;
  const onGrid = metered ? feeders.some((f) => f.role === 'utility_feed' && f.reading && (f.kw ?? 0) > IDLE_KW) : mains?.live === true;
  const onGen = gens.some((g) => g.running === true);
  const source: Supply | null =
    !gridRead && gens.every((g) => g.running === null)
      ? null
      : onGrid && onGen
        ? 'both'
        : onGen
          ? 'generator'
          : onGrid
            ? 'grid'
            : /* The mains read dead, no set carrying the load, and the batteries proven to be giving
                 charge: the room runs on its batteries — the strip's own evidence, never a guess. */
              gridRead && batteryDischarging
              ? 'battery'
              : grid.unread.length > 0 || genUnread.length > 0
              ? null
              : 'none';
  const unread = [...grid.unread, ...ups.unread, ...genUnread.map((g) => g.name)];
  if (!metered && belowKw !== null) return { source, gridKw: null, generatorKw, siteKw: belowKw, measuredAt: 'loads', upsKw: ups.kw, unread };
  const siteKw = grid.kw === null && metered ? null : genUnread.length > 0 ? null : sum([grid.kw, generatorKw]);
  return { source, gridKw: grid.kw, generatorKw, siteKw, measuredAt: grid.kw === null ? null : 'incomers', upsKw: ups.kw, unread };
}

/* The operator's words (OPERATOR-DASHBOARDS.md §3, 2026-10-21): Mains and DG, never "grid" and "generator". */
export const SUPPLY_WORDS: Record<Supply, string> = {
  grid: 'Mains',
  generator: 'DG',
  both: 'Mains and DG',
  battery: 'Batteries',
  none: 'No source read',
};

export interface Check {
  value: number | null;
  warn: boolean;
}

/** The supply's quality across the grid incomers that are reading: the worst of each, marked out of band. */
export function supplyQuality(feeders: readonly FeederNow[]): { volts: Check; hz: Check; pf: Check } {
  const live = feeders.filter((f) => f.role === 'utility_feed' && f.reading);
  const volts = live.map((f) => f.volts).filter((v): v is number => v !== null);
  const hz = live.map((f) => f.hz).filter((v): v is number => v !== null);
  const loaded = live.filter((f) => (f.kw ?? 0) > IDLE_KW);
  const pf = loaded.map((f) => f.pf).filter((v): v is number => v !== null);
  /* The reading furthest from the middle of its band is the one shown: one bad incomer must not be
     averaged away by a good one. */
  const worst = (vs: number[], [lo, hi]: readonly [number, number]): Check => {
    if (vs.length === 0) return { value: null, warn: false };
    const mid = (lo + hi) / 2;
    const v = vs.reduce((a, b) => (Math.abs(b - mid) > Math.abs(a - mid) ? b : a));
    return { value: v, warn: v < lo || v > hi };
  };
  const lowPf = pf.length === 0 ? null : Math.min(...pf);
  return {
    volts: worst(volts, LINE_VOLTS_BAND),
    hz: worst(hz, HZ_BAND),
    pf: { value: lowPf, warn: lowPf !== null && lowPf < PF_FLOOR },
  };
}

/* ─────────────────────────────────────────── the Overview's graphics (2026-10-16) ── */

/**
 * The site load split into what the UPS carries and the rest — the bar under Supply now. The UPS
 * feeders are downstream of the incomers, so the UPS load is a PART of the site load, never added
 * to it. Null while there is no load to split: a bar of nothing would read as a figure.
 */
export function loadMix(s: SupplyNow): { upsKw: number; restKw: number; upsShare: number } | null {
  if (s.siteKw === null || s.siteKw <= IDLE_KW || s.upsKw === null) return null;
  const upsKw = Math.max(0, s.upsKw);
  const upsShare = Math.min(1, upsKw / s.siteKw);
  return { upsKw, restKw: Math.max(0, s.siteKw - upsKw), upsShare };
}

/** A reading on a band gauge: where it sits on the scale, where the band is, and whether it is out. */
export interface BandGauge {
  /** 0 at the scale's low end, 1 at its high; a reading beyond the scale is pinned to its end. */
  at: number;
  bandFrom: number;
  bandTo: number;
  out: boolean;
}

/** Positions on a gauge drawn over `scale`, with the acceptable `band` shaded. Null with no reading. */
export function bandGauge(value: number | null, scale: readonly [number, number], band: readonly [number, number]): BandGauge | null {
  if (value === null || !Number.isFinite(value)) return null;
  const [lo, hi] = scale;
  const pos = (v: number): number => Math.min(1, Math.max(0, (v - lo) / (hi - lo)));
  return { at: pos(value), bandFrom: pos(band[0]), bandTo: pos(band[1]), out: value < band[0] || value > band[1] };
}

export interface FeederGroup {
  role: FeederRole;
  /** The group's draw now, summed — only where the members do not overlap (see `feederGroups`). */
  kw: number | null;
  feeders: Array<FeederNow & { fraction: number | null }>;
}

/**
 * The feeders as bars, grouped by role, every bar on ONE scale (the busiest feeder's draw) so a
 * UPS feeder is read against an incomer, not stretched to its own width. The grid incomers' sum is
 * the site's draw and the UPS feeders' the UPS load; the other panels get no sum, because a panel's
 * meter can sit upstream of feeders already counted (NBR's 7B incomer carries its UPS feeders).
 */
export function feederGroups(feeders: readonly FeederNow[]): FeederGroup[] {
  const top = Math.max(0, ...feeders.filter((f) => f.reading && f.kw !== null).map((f) => f.kw as number));
  const order: FeederRole[] = ['utility_feed', 'ups', 'pdu', 'switchgear'];
  return order
    .map((role) => {
      const members = feeders.filter((f) => f.role === role);
      return {
        role,
        /* Every member or nothing: a group with one feeder not read has no total, never a smaller one. */
        kw: role === 'switchgear' || members.some((f) => !f.reading || f.kw === null) ? null : sum(members.map((f) => f.kw)),
        feeders: members.map((f) => ({
          ...f,
          fraction: !f.reading || f.kw === null || top <= 0 ? null : Math.min(1, Math.max(0, f.kw / top)),
        })),
      };
    })
    .filter((g) => g.feeders.length > 0);
}

/** Minutes as words: "0 min", "45 min", "3 h 20 min". */
export function minutesWords(min: number | null): string | null {
  if (min === null) return null;
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
}

/** A counter's first reading this close to the site's midnight starts the day; later, the day is read in part. */
export const RUN_READ_SLACK_MS = 30 * 60_000;

export interface RunToday {
  /** Minutes the engine ran in what was read today. */
  minutes: number;
  /** Where today's reading began, when that was after the day did (a part day); else null. */
  partFrom: number | null;
  /** Where it ended, when the controller has been quiet since; else null. */
  partTo: number | null;
}

/**
 * RUN TODAY — how long the engine ran since the site's midnight, from its controller's cumulative
 * `runtime` counter: the latest reading less the first, in the unit its parameter states
 * (`hoursFromUnit`). Read in quarter-hour `max` and `min` buckets from the site's midnight, as
 * Energy today reads its counters, never a '1 day' bucket (a UTC day).
 *
 * THE ENGINE'S COUNTER, NOT THE GATEWAY'S (2026-10-20, measured). This was the gateway's
 * `counters_time_on_generator_today_m`, which stayed 0 through every run it saw: on 16 Oct, engine
 * at 1,500 rpm for half an hour, `runtime` rose 1,800 s and it read 0; on prod, 00:00 to 06:00 in
 * Kigali on 20 Oct, `runtime` rose 21,960 s and the card said "Run today 0 min".
 *
 * A day read from late is a part figure and says so (`runLabel`), as Energy today's "since 7:15 AM"
 * does: what ran before the first reading is not today's to claim, nor anyone's to guess. A counter
 * that went backwards (a reset, a replaced controller) is no figure, never a negative one; no unit
 * stated is no figure either. Null: the row is not shown.
 */
export function runTodayOf(
  maxRange: RangeResponse | undefined,
  minRange: RangeResponse | undefined,
  seriesId: number | null,
  unit: string | null | undefined,
  midnightMs: number,
  nowMs: number,
  bucketMs = 15 * 60_000,
): RunToday | null {
  if (seriesId === null) return null;
  const read = (range: RangeResponse | undefined): Map<number, number> =>
    new Map(
      (range?.series.find((s) => s.seriesId === seriesId)?.points ?? [])
        .map((p) => [Date.parse(p.t), p.v] as const)
        .filter((e): e is readonly [number, number] => e[1] !== null && Number.isFinite(e[1]) && e[0] >= midnightMs),
    );
  const hi = read(maxRange);
  const lo = read(minRange);
  const ts = [...new Set([...hi.keys(), ...lo.keys()])].sort((a, b) => a - b);
  const first = ts[0];
  const last = ts[ts.length - 1];
  if (first === undefined || last === undefined) return null;
  /* Every bucket at or above all before it: a counter that fell was reset, and spans nothing. */
  let top = -Infinity;
  for (const t of ts) {
    if ((lo.get(t) ?? hi.get(t) ?? Infinity) < top) return null;
    top = Math.max(top, hi.get(t) ?? lo.get(t) ?? -Infinity);
  }
  const start = lo.get(first) ?? hi.get(first);
  const end = hi.get(last) ?? lo.get(last);
  if (start === undefined || end === undefined) return null;
  const hours = hoursFromUnit(end - start, unit);
  if (hours === null) return null;
  return {
    minutes: hours * 60,
    partFrom: first > midnightMs + RUN_READ_SLACK_MS ? first : null,
    partTo: last + bucketMs < nowMs - RUN_READ_SLACK_MS ? last + bucketMs : null,
  };
}

/** "Run today", "Run since 7:15 AM", "Run today, to 3:00 AM", "Run 7:15 AM to 9:00 AM". */
export function runLabel(run: RunToday, clock: (t: number) => string): string {
  if (run.partFrom !== null && run.partTo !== null) return `Run ${clock(run.partFrom)} to ${clock(run.partTo)}`;
  if (run.partFrom !== null) return `Run since ${clock(run.partFrom)}`;
  if (run.partTo !== null) return `Run today, to ${clock(run.partTo)}`;
  return 'Run today';
}

/** Buckets summed across a group's series; a bucket some series missed is a gap, never a dip. */
export function groupSum(range: RangeResponse | undefined, ids: ReadonlySet<number>): Array<{ t: number; v: number | null }> {
  const series = (range?.series ?? []).filter((s) => ids.has(s.seriesId));
  if (series.length === 0) return [];
  const byT = new Map<number, { n: number; v: number }>();
  for (const s of series) {
    for (const p of s.points) {
      if (p.v === null) continue;
      const t = Date.parse(p.t);
      const cur = byT.get(t) ?? { n: 0, v: 0 };
      byT.set(t, { n: cur.n + 1, v: cur.v + p.v });
    }
  }
  return [...byT.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, x]) => ({ t, v: x.n === series.length ? x.v : null }));
}

/**
 * Midnight today in the site's own zone, as an instant. A data centre's day is the site's day: an
 * operator in Kigali reading "Load today" from a laptop set to another zone must still see Kigali's
 * day, from its midnight. With no zone declared, the viewer's own midnight, as every other card.
 */
export function midnightIn(timeZone: string | undefined, nowMs: number): number {
  if (!timeZone) {
    const d = new Date(nowMs);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(nowMs);
  const part = (t: string): number => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const intoDay = ((part('hour') * 60 + part('minute')) * 60 + part('second')) * 1000 + (nowMs % 1000);
  return nowMs - intoDay;
}

/**
 * Energy over a window from cumulative kWh counters: each counter's highest reading in the window
 * less its lowest, summed. Null when any counter has no reading in the window — a total missing an
 * incomer is not the site's energy.
 */
export function energyBetween(
  maxRange: RangeResponse | undefined,
  minRange: RangeResponse | undefined,
  seriesIds: readonly number[],
): number | null {
  if (seriesIds.length === 0 || !maxRange || !minRange) return null;
  let total = 0;
  for (const id of seriesIds) {
    const hi = maxRange.series.find((s) => s.seriesId === id)?.points.map((p) => p.v).filter((v): v is number => v !== null) ?? [];
    const lo = minRange.series.find((s) => s.seriesId === id)?.points.map((p) => p.v).filter((v): v is number => v !== null) ?? [];
    if (hi.length === 0 || lo.length === 0) return null;
    total += Math.max(0, Math.max(...hi) - Math.min(...lo));
  }
  return total;
}

/* ───────────────────────────── a telecom power room's own equipment (2026-10-20, JAZZ MSC5) ── */

/* Fresh rows only, through `freshOf` above: a unit that has gone quiet is `reading: false` and its
   figures are null, never its last values as now. */

const minOf = (vs: ReadonlyArray<number | null>): number | null => {
  const got = vs.filter((v): v is number => v !== null);
  return got.length === 0 ? null : Math.min(...got);
};

/** A DC plant (role `rectifier` with a load reading): a -48 V telecom plant's rectifiers and bus. */
export interface DcPlantNow {
  deviceId: number;
  /** The device's own name ("Vertive DC Plant-1 M830D"): its tooltip, its aria-label, its Open button. */
  name: string;
  /** What every DC screen calls it: "DC system 1" (`dcSystemNames`). */
  label: string;
  busVolts: number | null;
  loadAmps: number | null;
  loadKw: number | null;
  /** How many rectifier modules the controller counts as working. */
  rectifiers: number | null;
  tempC: number | null;
  /** The plant's AC input, the mean of its line voltages. */
  acVolts: number | null;
  reading: boolean;
}

/**
 * WHAT A -48 V SYSTEM IS CALLED ON SCREEN (Hamzah, 2026-10-20): "DC system 1", "DC system 2", never
 * "Plant-1" and never "plant", which reads as the solar plant, a thing a data centre does not have. The
 * number is the device's own where its name carries one after plant, system or DC ("Vertive DC Plant-1
 * M830D" is 1) and every system's differs; otherwise their order by id. Never a site's numbering: a
 * third system is "DC system 3". The device's own name stays its tooltip, aria-label and Open button.
 */
export function dcSystemNames(systems: ReadonlyArray<{ id: number; name: string }>): Map<number, string> {
  const own = systems.map((s) => {
    const m = /(?:plant|system|dc)[\s_#-]*(\d+)/i.exec(s.name);
    return m ? Number(m[1]) : null;
  });
  const usable = own.every((n) => n !== null) && new Set(own).size === own.length;
  const ordered = [...systems].sort((a, b) => a.id - b.id);
  return new Map(systems.map((s, i) => [s.id, `DC system ${usable ? own[i] : ordered.findIndex((o) => o.id === s.id) + 1}`]));
}

/** Every DC plant on the site, now. A charger that reports no load power is the DC supply card's, not this. */
export function dcPlantsNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): DcPlantNow[] {
  /* The -48 V systems by the one rule the Power flow and the single line place them by (`dc-chain.ts`). */
  const ids = new Set(dcSystemIds(rows, devices));
  const systems = devices.filter((d) => ids.has(d.id));
  const labels = dcSystemNames(systems);
  return systems
    .map((d) => {
      const { m, reading } = freshOf(rows, d.id);
      return {
        deviceId: d.id,
        name: d.name,
        label: labels.get(d.id) ?? d.name,
        busVolts: one(m, 'dpp_channels_voltage'),
        loadAmps: one(m, 'dpp_channels_current'),
        loadKw: one(m, 'dc_load_power'),
        rectifiers: one(m, 'rectifiers_working'),
        tempC: one(m, 'dpp_channels_temperature'),
        acVolts: mean(m.get('ac_input_line_volts') ?? []),
        reading,
      };
    });
}

/** A UPS that reports its own load (role `ups`, no power reading of its own). */
export interface UpsUnitNow {
  deviceId: number;
  name: string;
  /** The most loaded phase's share of the unit's rating, in percent. */
  loadPct: number | null;
  inVolts: number | null;
  outVolts: number | null;
  inAmps: number | null;
  outAmps: number | null;
  /** The lowest output power factor across the phases. */
  pf: number | null;
  inHz: number | null;
  outHz: number | null;
  reading: boolean;
}

export function upsUnitsNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): UpsUnitNow[] {
  const withLoad = new Set(rows.filter((r) => r.baseKey === 'ups_load_pct').map((r) => r.deviceId));
  return devices
    .filter((d) => d.role === 'ups' && withLoad.has(d.id))
    .map((d) => {
      const { m, reading } = freshOf(rows, d.id);
      return {
        deviceId: d.id,
        name: d.name,
        loadPct: max(m.get('ups_load_pct') ?? []),
        inVolts: one(m, 'ups_input_volts'),
        outVolts: one(m, 'ups_output_volts'),
        inAmps: one(m, 'ups_input_amps'),
        outAmps: one(m, 'ups_output_amps'),
        pf: minOf(m.get('ups_output_pf') ?? []),
        inHz: one(m, 'ups_input_hz'),
        outHz: one(m, 'ups_output_hz'),
        reading,
      };
    });
}

/** A battery system (role `battery`): its state of charge and health, and its weakest module. */
export interface BatteryNow {
  deviceId: number;
  name: string;
  socPct: number | null;
  sohPct: number | null;
  volts: number | null;
  /** Positive charging, negative discharging, as the system reports it. */
  amps: number | null;
  capacityAh: number | null;
  cabinets: Array<{ socPct: number | null; sohPct: number | null; volts: number | null; amps: number | null }>;
  /** The lowest health of any one module: the one that ages the string. */
  weakestModuleSohPct: number | null;
  modules: number;
  reading: boolean;
}

export function batteriesNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): BatteryNow[] {
  const withSoc = new Set(rows.filter((r) => r.baseKey === 'battery_soc' || r.baseKey === 'cabinet_soc').map((r) => r.deviceId));
  return devices
    .filter((d) => d.role === 'battery' && withSoc.has(d.id))
    .map((d) => {
      const { m, reading } = freshOf(rows, d.id);
      const soc = m.get('cabinet_soc') ?? [];
      const n = Math.max(soc.length, (m.get('cabinet_volts') ?? []).length);
      const at = (k: string, i: number): number | null => m.get(k)?.[i] ?? null;
      /* Modules come as one key per cabinet, each with its own instances: every reading, flattened. */
      const moduleSoh = rows.filter((r) => r.deviceId === d.id && !r.stale && r.baseKey === 'module_soh').map((r) => num(r.value));
      return {
        deviceId: d.id,
        name: d.name,
        socPct: one(m, 'battery_soc') ?? mean(soc),
        sohPct: one(m, 'battery_soh') ?? mean(m.get('cabinet_soh') ?? []),
        volts: one(m, 'battery_volts') ?? mean(m.get('cabinet_volts') ?? []),
        amps: one(m, 'battery_amps') ?? sum(m.get('cabinet_amps') ?? []),
        capacityAh: one(m, 'battery_capacity_ah'),
        cabinets: Array.from({ length: n }, (_, i) => ({
          socPct: at('cabinet_soc', i),
          sohPct: at('cabinet_soh', i),
          volts: at('cabinet_volts', i),
          amps: at('cabinet_amps', i),
        })),
        weakestModuleSohPct: minOf(moduleSoh),
        modules: moduleSoh.length,
        reading,
      };
    });
}

/** A room sensor (role `sensor` with a temperature or humidity). */
export interface RoomNow {
  deviceId: number;
  name: string;
  tempC: number | null;
  humidityPct: number | null;
  reading: boolean;
}

export function roomsNow(rows: readonly LatestRow[], devices: readonly DeviceRole[]): RoomNow[] {
  const has = new Set(rows.filter((r) => r.baseKey === 'room_temperature' || r.baseKey === 'room_humidity').map((r) => r.deviceId));
  return devices
    .filter((d) => d.role === 'sensor' && has.has(d.id))
    .map((d) => {
      const { m, reading } = freshOf(rows, d.id);
      return { deviceId: d.id, name: d.name, tempC: one(m, 'room_temperature'), humidityPct: one(m, 'room_humidity'), reading };
    });
}

/*
 * BANDS for the power room's own readings, each with where it comes from:
 *   - the room: ASHRAE TC 9.9 recommended envelope for IT equipment, 18 to 27 °C, and up to 60 % RH;
 *   - a UPS: marked at 80 % load, the share past which a unit loses its headroom to carry its pair's
 *     load if that one trips (a 2N pair each at 50 % is the design point);
 *   - a battery's health: marked under 80 %, the state of health at which lithium cells are
 *     conventionally at end of life;
 *   - a -48 V plant's bus: marked outside ETSI EN 300 132-2's normal service range, 40.5 to 57 V.
 *     Whether it floats, boosts or sags inside that range is read from its own week (`floatLevel`),
 *     never from a fixed figure, because a lead-acid and a lithium plant float at different voltages.
 */
export const ROOM_TEMP_BAND = [18, 27] as const;
export const ROOM_RH_MAX = 60;
export const UPS_LOAD_MAX_PCT = 80;
export const BATTERY_SOH_MIN_PCT = 80;
export const DC_BUS_BAND = [40.5, 57] as const;

/**
 * WHICH BUS A BATTERY IS ON, read from its own voltage (2026-10-20). A battery system above the
 * -48 V bus's ceiling cannot sit on that bus: JAZZ's two SmartLi systems read 545 V against plants
 * floating at 53.5 V, so they are the UPS units' batteries, and the first board drew them under DC
 * power beside the plants, which the readings rule out. At or below the ceiling it is the plant's (a
 * discharged -48 V string reads under 40.5 V and is still on that bus). A battery that reports no
 * voltage keeps the DC stage, as before: nothing says otherwise. Which UPS it serves no reading
 * says; that is the single line's to draw.
 */
export function batteryBus(b: Pick<BatteryNow, 'volts'>): 'dc' | 'ups' {
  return b.volts !== null && b.volts > DC_BUS_BAND[1] ? 'ups' : 'dc';
}

/* ───────────────────────────────────────────── what the site's load is, for every analytic ── */

/** A UPS that reports its own load and no power reading: a UPS unit, never a feeder meter. */
export function isUpsUnit(rows: readonly LatestRow[], deviceId: number): boolean {
  const own = rows.filter((r) => r.deviceId === deviceId);
  return own.some((r) => r.baseKey === 'ups_load_pct') && !own.some((r) => r.baseKey === 'total_system_power' || r.baseKey === 'active_power');
}

/**
 * The series whose bucket-by-bucket SUM is the site's load, and what that load is: the grid incomers
 * where they are metered (`'incomers'`); else what is metered below them, the PDUs' input power and
 * the DC plants' load (`'loads'`), two branches that never overlap. Every Demand card reads this, so
 * a telecom site with no incomer meter gets the same heat map, daily demand and load duration as a
 * bank's, over the load it can measure, and says so.
 */
export function loadSeries(
  rows: readonly LatestRow[],
  devices: readonly DeviceRole[],
): { ids: number[]; measuredAt: 'incomers' | 'loads' | null } {
  const roleOf = new Map(devices.map((d) => [d.id, d.role]));
  const of = (role: string, key: string): number[] =>
    rows.filter((r) => roleOf.get(r.deviceId) === role && r.baseKey === key).map((r) => r.seriesId);
  const incomers = of('utility_feed', 'total_system_power');
  if (incomers.length > 0) return { ids: incomers, measuredAt: 'incomers' };
  const below = [...of('pdu', 'total_system_power'), ...of('rectifier', 'dc_load_power')];
  return { ids: below, measuredAt: below.length > 0 ? 'loads' : null };
}

/**
 * Series added bucket by bucket: a bucket some series missed is a gap (null), never a partial sum,
 * so a meter that skipped a poll never reads as the load falling. Sorted by time.
 */
export function bucketSums(range: RangeResponse | undefined, ids: readonly number[]): Array<{ t: number; v: number | null }> {
  return groupSum(range, new Set(ids));
}
